/**
 * `<FeedGraph>` — a feed's relationships, drawn beside the table that lists them.
 *
 * The transactions page's Graph tab and the overview's on-chain panel are the
 * same surface with different data: a canvas in a bordered box of a fixed
 * height, reached through the same four states (loading → error → nothing to
 * draw → the graph), with the same layout toggle on the same stored preference.
 * Both had written all of it out, which is how the two drifted to different
 * empty-state boxes and the same `["hierarchical", "temporal"]` twice.
 *
 * What is shared is the SHAPE; what each host still owns is its data, its copy
 * and how tall the box is:
 *
 *   <FeedGraph
 *     nodes={elements.nodes} edges={elements.edges}
 *     heightClassName={GRAPH_HEIGHT}
 *     emptyTitle="No relationships to graph" emptyDescription="…"
 *     onNodeClick={onNodeClick}
 *   />
 *
 * Sizing is `<GraphFrame>`'s, as everywhere else: this passes the box's classes
 * to it and the canvas fills whatever they add up to.
 */
import * as React from "react";
import { Activity } from "lucide-react";
import { GraphCanvas, type TxGraphEdge, type TxGraphNode } from "@authe/tx-graph";
import {
  GRAPH_FLOW_VIEW_MODES,
  resolveGraphViewMode,
  type GraphViewMode,
} from "@authe/fe-commons/display";
import { EmptyState, ErrorState, GraphFrame, Skeleton, cn, useDisplayPref } from "@authe/ui-core";
import { displayPrefs } from "@/app/prefs";

/**
 * The stored graph layout mode, narrowed to what a canvas surface can draw.
 *
 * One preference for every graph, so it can name `force` — a mode that needs the
 * optional heavy engine this app does not ship. `resolveGraphViewMode` is what
 * keeps that from reaching a canvas; the pref itself is shared, so the narrowing
 * belongs with it rather than at each reader.
 *
 * Exported because a host that lets a saved view snapshot the mode has to lift
 * it above the graph (the on-chain panel does); one that doesn't can leave it to
 * `<FeedGraph>`.
 */
export function useGraphViewModePref(): [GraphViewMode, (mode: GraphViewMode) => void] {
  const [stored, setMode] = useDisplayPref<GraphViewMode>(
    () => displayPrefs.getGraphViewMode(),
    (m) => displayPrefs.setGraphViewMode(m),
  );
  return [resolveGraphViewMode(stored, GRAPH_FLOW_VIEW_MODES), setMode];
}

export interface FeedGraphProps {
  nodes: readonly TxGraphNode[];
  edges: readonly TxGraphEdge[];
  /** Tailwind height for the box — the host decides how tall its surface is. */
  heightClassName: string;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onNodeClick?: (node: TxGraphNode) => void;
  /**
   * Controlled layout mode. Omitted, the shared preference decides — pass it
   * only where something above the graph needs to read the mode too.
   */
  mode?: GraphViewMode;
  onModeChange?: (mode: GraphViewMode) => void;
  /**
   * The drawn box, for a host whose export menu rasterises it. A ref OBJECT, not
   * the wider `React.Ref`: the app and the linked packages resolve their own
   * `@types/react`, and only the object form is structurally compatible across
   * the two (a callback ref carries a branded void type that is not).
   */
  boxRef?: React.RefObject<HTMLDivElement | null>;
  emptyTitle: string;
  emptyDescription: React.ReactNode;
  /**
   * Rendered above the canvas, and ONLY when there is one — a note about what is
   * being drawn has nothing to say over an empty state or a spinner.
   */
  canvasHeader?: React.ReactNode;
}

export function FeedGraph({
  nodes,
  edges,
  heightClassName,
  isLoading,
  error,
  onRetry,
  onNodeClick,
  mode: controlledMode,
  onModeChange,
  boxRef,
  emptyTitle,
  emptyDescription,
  canvasHeader,
}: FeedGraphProps): React.ReactElement {
  const [prefMode, setPrefMode] = useGraphViewModePref();
  const mode = controlledMode ?? prefMode;
  const setMode = onModeChange ?? setPrefMode;

  if (isLoading) {
    return <Skeleton className={cn("w-full rounded-md", heightClassName)} />;
  }
  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />;
  }
  if (nodes.length === 0) {
    return (
      <div className={cn("flex items-center justify-center rounded-md border", heightClassName)}>
        <EmptyState
          icon={<Activity className="h-6 w-6" />}
          title={emptyTitle}
          description={emptyDescription}
        />
      </div>
    );
  }
  const canvas = (
    <GraphFrame
      boxRef={boxRef}
      className={cn("overflow-hidden rounded-md border", heightClassName)}
    >
      <GraphCanvas
        nodes={nodes as TxGraphNode[]}
        edges={edges as TxGraphEdge[]}
        viewMode={mode}
        onViewModeChange={setMode}
        showViewModeToggle
        viewModes={GRAPH_FLOW_VIEW_MODES}
        onNodeClick={onNodeClick}
        showControls
        showMiniMap={false}
        fitView
      />
    </GraphFrame>
  );
  if (!canvasHeader) return canvas;
  return (
    <div className="space-y-2">
      {canvasHeader}
      {canvas}
    </div>
  );
}
